import type { FC, ReactNode } from 'react'
import type { CourseDetail } from '@/types'

import {
    ArrowRight,
    CalendarDays,
    CheckCircle2,
    Clock,
    Layers,
    MonitorSmartphone
} from 'lucide-react'
import { enrollHref } from '@/lib/routes'
import { ButtonLink } from '@/components/generals/button-link'

interface CourseEnrollCardProps {
    course: CourseDetail
}

const INCLUDED: Array<string> = [
    "Your own computer in every class",
    "Notes and practice files to keep",
    "Help from your instructor between lessons",
    "Certificate of completion"
]

export const CourseEnrollCard: FC<CourseEnrollCardProps> = ({ course }): ReactNode => {
    return (
        <div className='rounded-3xl border bg-card p-6 shadow-xl shadow-foreground/5 sm:p-7'>
            {course.price && (
                <div>
                    <p className='font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase'>
                        Course fee
                    </p>

                    <p className='mt-1 font-heading text-4xl font-semibold tracking-tight'>
                        <span data-mdlfy-cms={`courses:${course.id}:price`}>{course.price}</span>
                    </p>
                </div>
            )}

            <ul className='mt-6 space-y-3 text-sm'>
                {course.duration && (
                    <li className='flex items-start gap-3'>
                        <Clock className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                        <span data-mdlfy-cms={`courses:${course.id}:duration`}>{course.duration}</span>
                    </li>
                )}

                {course.schedule && (
                    <li className='flex items-start gap-3'>
                        <CalendarDays className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                        <span data-mdlfy-cms={`courses:${course.id}:schedule`}>{course.schedule}</span>
                    </li>
                )}

                {course.format && (
                    <li className='flex items-start gap-3'>
                        <MonitorSmartphone className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                        <span data-mdlfy-cms={`courses:${course.id}:format`}>{course.format}</span>
                    </li>
                )}

                {course.modules.length > 0 && (
                    <li className='flex items-start gap-3'>
                        <Layers className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                        <span>
                            {course.modules.length} {course.modules.length === 1 ? "module" : "modules"}
                        </span>
                    </li>
                )}
            </ul>

            <ButtonLink href={enrollHref(course.slug)} size='lg' className='mt-7 w-full'>
                Enroll in this course
                <ArrowRight aria-hidden='true' />
            </ButtonLink>

            <p className='mt-3 text-center text-xs leading-relaxed text-muted-foreground'>
                Free to apply. An advisor confirms your batch and fee before you pay anything.
            </p>

            <div className='mt-6 border-t pt-6'>
                <p className='text-sm font-semibold'>
                    Included with every course
                </p>

                <ul className='mt-3 space-y-2.5 text-sm text-muted-foreground'>
                    {INCLUDED.map(item => (
                        <li key={item} className='flex items-start gap-2.5'>
                            <CheckCircle2 className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                            {item}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}
